import {
  Grid,
  NumberInput,
  Textarea,
  TextInput,
} from "@mantine/core";
import { measurementUnitApi, productCategoryApi } from "apis";
import { CreatableSelect } from "components";
import { MeasurementUnitResponse } from "constants/types/measurement";
import { ProductCategoryResponse } from "constants/types/productCategory";
import { ProductRequestBodyType } from "constants/types/product";
import { FormikProps } from "formik";
import { FC } from "react";
import { getInitials } from "utils/helpers/string";
import { useNotification } from "utils/hooks";
import ProductImageInput from "../ProductImageInput/ProductImageInput";

interface SelectOption {
  label: string;
  value: string;
}

interface ProductFormFieldsProps {
  formik: FormikProps<ProductRequestBodyType>;
  measurementOptions: SelectOption[];
  productCategoryOptions: SelectOption[];
  onMeasurementMutate: () => void;
  onCategoryMutate: () => void;
  currentImageKey?: string | null;
  selectedImageFile?: File | null;
  onImageFileChange: (file: File | null) => void;
}

const spanHalf = { base: 12, md: 6 };

const ProductFormFields: FC<ProductFormFieldsProps> = ({
  formik,
  measurementOptions,
  productCategoryOptions,
  onMeasurementMutate,
  onCategoryMutate,
  currentImageKey,
  selectedImageFile,
  onImageFileChange,
}) => {
  const { notification } = useNotification();

  const getFieldError = (field: keyof ProductRequestBodyType) =>
    formik.touched[field] && formik.errors[field]
      ? (formik.errors[field] as string)
      : null;

  return (
    <Grid>
      <Grid.Col span={12}>
        <ProductImageInput
          currentImageKey={currentImageKey}
          selectedFile={selectedImageFile}
          onFileChange={onImageFileChange}
        />
      </Grid.Col>

      <Grid.Col span={spanHalf}>
        <TextInput
          label="Mã mặt hàng"
          placeholder="VD: MH-001"
          {...formik.getFieldProps("code")}
          error={getFieldError("code")}
        />
      </Grid.Col>

      <Grid.Col span={spanHalf}>
        <TextInput
          label="Tên mặt hàng"
          placeholder="Nhập tên mặt hàng"
          withAsterisk
          {...formik.getFieldProps("name")}
          error={getFieldError("name")}
        />
      </Grid.Col>

      <Grid.Col span={spanHalf}>
        <TextInput
          label="Mô tả"
          placeholder="VD: Cáp dài 1m, màu đen"
          {...formik.getFieldProps("spec")}
          error={getFieldError("spec")}
        />
      </Grid.Col>

      <Grid.Col span={spanHalf}>
        <CreatableSelect
          label="Đơn vị tính"
          placeholder="Chọn đơn vị tính"
          withAsterisk
          data={measurementOptions}
          value={formik.values.unitId}
          onChange={(value) => formik.setFieldValue("unitId", value)}
          onBlur={() => formik.setFieldTouched("unitId", true)}
          error={getFieldError("unitId")}
          onCreate={async (query) => {
            try {
              const res = await measurementUnitApi.create({
                name: query,
                code: getInitials(query),
              });
              const data: MeasurementUnitResponse = res.data.data;
              notification.success({
                title: `Tạo đơn vị tính "${query}" thành công`,
              });
              onMeasurementMutate();
              return { label: data.name, value: data.id };
            } catch (error: any) {
              notification.error({
                title: "Tạo đơn vị tính thất bại",
                message: error?.error?.message,
              });
              throw error;
            }
          }}
        />
      </Grid.Col>

      <Grid.Col span={spanHalf}>
        <CreatableSelect
          label="Nhóm hàng hóa"
          placeholder="Chọn nhóm hàng hóa"
          withAsterisk
          data={productCategoryOptions}
          value={formik.values.categoryId}
          onChange={(value) => formik.setFieldValue("categoryId", value)}
          onBlur={() => formik.setFieldTouched("categoryId", true)}
          error={getFieldError("categoryId")}
          onCreate={async (query) => {
            try {
              const res = await productCategoryApi.create({
                name: query,
                code: getInitials(query),
              });
              const data: ProductCategoryResponse = res.data.data;
              notification.success({
                title: `Tạo nhóm hàng hóa "${query}" thành công`,
              });
              onCategoryMutate();
              return { label: data.name, value: data.id };
            } catch (error: any) {
              notification.error({
                title: "Tạo nhóm hàng hóa thất bại",
                message: error?.error?.message,
              });
              throw error;
            }
          }}
        />
      </Grid.Col>

      <Grid.Col span={spanHalf}>
        <NumberInput
          label="Giá Net"
          placeholder="Nhập giá Net"
          withAsterisk
          min={0}
          thousandSeparator="."
          decimalSeparator=","
          suffix=" ₫"
          value={formik.values.priceNet}
          onChange={(value) => formik.setFieldValue("priceNet", value)}
          onBlur={() => formik.setFieldTouched("priceNet", true)}
          error={getFieldError("priceNet")}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <Textarea
          label="Ghi chú"
          placeholder="Nhập ghi chú (tuỳ chọn)"
          autosize
          minRows={2}
          {...formik.getFieldProps("notes")}
          error={getFieldError("notes")}
        />
      </Grid.Col>
    </Grid>
  );
};

export default ProductFormFields;
